import type { ParagraphHeroCta as ParagraphHeroCtaData } from "@/lib/api/cms-catalog";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import { HtmlText } from "../shared/HtmlText";
import styles from "./ParagraphHeroCta.module.css";

/**
 * Layout 31 - `PARAGRAPH_HERO_CTA_LAYOUT`: hero strony głównej z CTA i statystykami.
 *
 * Struktura (wszystkie bloki opcjonalne poza tytułem):
 *   1. Etykieta z linią akcentu nad tytułem - eyebrow uppercase (text_1)
 *   2. Tytuł H1 z inline accent (name - HTML z `<strong>` lub `<span class="accent">`)
 *   3. Subtitle teal (text_2)
 *   4. Opis (text - HTML z TinyMCE)
 *   5. Boksy wyróżnień pod opisem (highlights - ikona + tytuł + tekst)
 *   6. 2 CTA buttons (primary green + secondary outline)
 *   7. 3 stat boxes (value + label)
 */
export function ParagraphHeroCta({
  paragraph,
}: {
  paragraph: ParagraphHeroCtaData;
  t: ParagraphTranslations;
}) {
  const {
    name,
    text_1: badge,
    text_2: subtitle,
    text: desc,
    button_name: btn1Label,
    button_url: btn1Url,
    button_name_1: btn2Label,
    button_url_1: btn2Url,
    stats,
    highlights,
  } = paragraph;

  const showCta1 = btn1Label && btn1Label.trim() !== "" && btn1Url && btn1Url.trim() !== "";
  const showCta2 = btn2Label && btn2Label.trim() !== "" && btn2Url && btn2Url.trim() !== "";
  const hasCta = showCta1 || showCta2;
  const hasStats = Array.isArray(stats) && stats.length > 0;
  const hasHighlights = Array.isArray(highlights) && highlights.length > 0;

  return (
    <section className={styles.hero}>
      {badge && badge.trim() !== "" ? (
        <p className={styles.eyebrow}>
          <span className={styles.eyebrowLine} aria-hidden="true" />
          {badge}
        </p>
      ) : null}

      {name ? (
        <h2 className={styles.title}>
          <HtmlText html={name} />
        </h2>
      ) : null}

      {subtitle && subtitle.trim() !== "" ? (
        <p className={styles.subtitle}>{subtitle}</p>
      ) : null}

      {desc && desc.trim() !== "" ? (
        <div className={styles.desc}>
          <HtmlText html={desc} />
        </div>
      ) : null}

      {hasHighlights ? (
        <ul className={styles.highlights}>
          {highlights.map((item, idx) => (
            <li key={idx} className={styles.highlight}>
              {item.icon ? (
                <i
                  className={`${styles.highlightIcon} nc-icon-outline ${item.icon}`}
                  aria-hidden="true"
                />
              ) : (
                <SparkleIcon className={styles.highlightIcon} />
              )}
              <div className={styles.highlightBody}>
                {item.title ? <strong className={styles.highlightTitle}>{item.title}</strong> : null}
                {item.text ? <p className={styles.highlightText}>{item.text}</p> : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {hasCta ? (
        <div className={styles.cta}>
          {showCta1 ? (
            <a href={btn1Url} className={styles.ctaPrimary}>
              {btn1Label}
            </a>
          ) : null}
          {showCta2 ? (
            <a href={btn2Url} className={styles.ctaSecondary}>
              {btn2Label}
            </a>
          ) : null}
        </div>
      ) : null}

      {hasStats ? (
        <ul className={styles.stats}>
          {stats.map((stat, idx) => (
            <li key={idx} className={styles.stat}>
              {stat.value ? <span className={styles.statValue}>{stat.value}</span> : null}
              {stat.label ? <span className={styles.statLabel}>{stat.label}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/**
 * Domyślna ikona boksu wyróżnienia (iskierki - duża gwiazdka + dwie małe),
 * używana gdy admin nie wybrał ikony Nucleo.
 */
function SparkleIcon({ className }: { className: string }) {
  return (
    <svg
      className={className}
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M10 3.5c.4 3.6 2.4 5.6 6 6-3.6.4-5.6 2.4-6 6-.4-3.6-2.4-5.6-6-6 3.6-.4 5.6-2.4 6-6Z" />
      <path d="M18 2c.2 1.5 1 2.3 2.5 2.5-1.5.2-2.3 1-2.5 2.5-.2-1.5-1-2.3-2.5-2.5C17 4.3 17.8 3.5 18 2Z" />
      <path d="M17.5 15.5c.2 1.6 1 2.4 2.5 2.5-1.5.2-2.3 1-2.5 2.5-.2-1.5-1-2.3-2.5-2.5 1.5-.1 2.3-.9 2.5-2.5Z" />
    </svg>
  );
}
